import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Modal from "@/components/Modal";
import { useI18n } from "@/context/I18nContext";
import { API } from "@/context/AuthContext";

/**
 * Generic CRUD panel for taxonomy entities (Product Categories,
 * Product Subcategories, and any future scale-ready taxonomy).
 *
 * Strictly UI-layer: validates the parent-cat selection for subcategories
 * but never touches another module's data. Future-ready fields (icon,
 * image_url, color_hex, sort_order) are NOT exposed in this UI but the
 * backend already persists them, so we can light them up later without
 * a schema change.
 *
 * Props:
 *   entity: "category" | "subcategory"
 *   parentCategories: list (only used when entity === "subcategory")
 *   testIdPrefix: string used to derive data-testid values
 */
export default function TaxonomyCrudPanel({
  entity,
  parentCategories,
  testIdPrefix,
}) {
  const { t } = useI18n();
  const isSub = entity === "subcategory";
  const resource = isSub ? "product-subcategories" : "product-categories";
  const TID = testIdPrefix;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm(isSub));
  const [saving, setSaving] = useState(false);

  function initialForm(sub) {
    return {
      name: "",
      code: "",
      description: "",
      is_active: true,
      ...(sub ? { category_id: "" } : {}),
    };
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/${resource}`, {
        withCredentials: true,
      });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  }, [resource]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(initialForm(isSub));
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || "",
      code: item.code || "",
      description: item.description || "",
      is_active: !!item.is_active,
      ...(isSub ? { category_id: item.category_id || "" } : {}),
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const name = (form.name || "").trim();
    if (!name) {
      toast.error(t("name_required"));
      return;
    }
    if (isSub && !form.category_id) {
      toast.error(t("product_taxonomy_pick_parent"));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name,
        code: (form.code || "").trim() || null,
        description: (form.description || "").trim() || null,
        is_active: form.is_active,
        ...(isSub ? { category_id: form.category_id } : {}),
      };
      if (editing?.id) {
        await axios.put(`${API}/${resource}/${editing.id}`, payload, {
          withCredentials: true,
        });
      } else {
        await axios.post(`${API}/${resource}`, payload, { withCredentials: true });
      }
      toast.success(t("saved_ok"));
      setOpen(false);
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (item) => {
    try {
      await axios.patch(`${API}/${resource}/${item.id}/toggle-active`, null, {
        withCredentials: true,
      });
      toast.success(t("state_changed_ok"));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const filtered = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    return items.filter((c) => {
      if (statusFilter === "active" && !c.is_active) return false;
      if (statusFilter === "inactive" && c.is_active) return false;
      if (isSub && categoryFilter !== "all" && c.category_id !== categoryFilter) {
        return false;
      }
      if (q) {
        const name = (c.name || "").toLowerCase();
        const code = (c.code || "").toLowerCase();
        if (!name.includes(q) && !code.includes(q)) return false;
      }
      return true;
    });
  }, [items, statusFilter, categoryFilter, search, isSub]);

  const parentName = (id) =>
    parentCategories?.find?.((p) => p.id === id)?.name || "—";

  return (
    <div data-testid={`${TID}-root`} className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-end gap-3 flex-wrap">
          {isSub && (
            <div>
              <label className="label-overline block mb-1">
                {t("smart_products_category")}
              </label>
              <select
                data-testid={`${TID}-filter-category`}
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="border border-[var(--border)] px-3 py-2 font-mono-tech text-sm bg-white"
              >
                <option value="all">{t("all")}</option>
                {(parentCategories || []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="label-overline block mb-1">{t("status")}</label>
            <select
              data-testid={`${TID}-filter-status`}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-[var(--border)] px-3 py-2 font-mono-tech text-sm bg-white"
            >
              <option value="all">{t("all_statuses")}</option>
              <option value="active">{t("active_only")}</option>
              <option value="inactive">{t("inactive_only")}</option>
            </select>
          </div>
          <div>
            <label className="label-overline block mb-1">{t("search")}</label>
            <input
              data-testid={`${TID}-search`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("smart_products_search_placeholder")}
              className="border border-[var(--border)] px-3 py-2 text-sm bg-white min-w-[220px]"
            />
          </div>
        </div>
        <button
          data-testid={`${TID}-new-btn`}
          onClick={openCreate}
          className="bg-[var(--text-primary)] text-white px-5 py-2.5 font-display font-bold tracking-wide hover:bg-[var(--primary)] transition-colors"
        >
          + {t("new")}
        </button>
      </div>

      {loading ? (
        <div className="font-mono-tech text-sm text-[var(--text-secondary)] py-8 text-center">
          LOADING…
        </div>
      ) : filtered.length === 0 ? (
        <div
          data-testid={`${TID}-empty`}
          className="border border-dashed border-[var(--border)] bg-white py-12 text-center text-[var(--text-secondary)]"
        >
          {t("no_results")}
        </div>
      ) : (
        <div
          data-testid={`${TID}-list`}
          className="border border-[var(--border)] bg-white divide-y divide-[var(--border)]"
        >
          {filtered.map((c) => (
            <div
              key={c.id}
              data-testid={`${TID}-row-${c.id}`}
              className={`grid grid-cols-12 gap-4 items-center px-5 py-4 hover:bg-[var(--bg)] ${
                c.is_active ? "" : "opacity-60"
              }`}
            >
              <div className="col-span-12 sm:col-span-5">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <div className="font-display text-lg font-bold tracking-tight">
                    {c.name}
                  </div>
                  {c.code ? (
                    <span className="font-mono-tech text-[0.65rem] tracking-wider bg-black text-white px-2 py-0.5">
                      {c.code}
                    </span>
                  ) : null}
                </div>
                {c.description ? (
                  <div className="text-xs text-[var(--text-secondary)] mt-1">
                    {c.description}
                  </div>
                ) : null}
              </div>
              <div className="col-span-6 sm:col-span-3">
                {isSub ? (
                  <span className="font-mono-tech text-[0.65rem] tracking-wider text-[var(--text-secondary)]">
                    {parentName(c.category_id)}
                  </span>
                ) : null}
              </div>
              <div className="col-span-6 sm:col-span-1">
                <span
                  className={`font-mono-tech text-[0.65rem] tracking-wider px-2 py-1 ${
                    c.is_active
                      ? "bg-black text-white"
                      : "border border-[var(--border)] text-[var(--text-secondary)]"
                  }`}
                >
                  {c.is_active ? t("active") : t("inactive")}
                </span>
              </div>
              <div className="col-span-12 sm:col-span-3 flex justify-end gap-2">
                <button
                  data-testid={`${TID}-edit-${c.id}`}
                  onClick={() => openEdit(c)}
                  className="border border-[var(--border)] px-3 py-1.5 text-sm font-mono-tech hover:bg-[var(--text-primary)] hover:text-white transition-colors"
                >
                  {t("edit")}
                </button>
                <button
                  data-testid={`${TID}-toggle-${c.id}`}
                  onClick={() => toggle(c)}
                  className="border border-[var(--border)] px-3 py-1.5 text-sm font-mono-tech hover:bg-[var(--text-primary)] hover:text-white transition-colors"
                >
                  {c.is_active ? t("deactivate") : t("activate")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setEditing(null);
        }}
        title={editing?.id ? t("edit") : t("new")}
        testId={`${TID}-form-modal`}
      >
        <form onSubmit={submit} className="space-y-4">
          {isSub && (
            <div>
              <label className="label-overline block mb-1">
                {t("smart_products_category")}
              </label>
              <select
                data-testid={`${TID}-form-category`}
                value={form.category_id}
                onChange={(e) =>
                  setForm({ ...form, category_id: e.target.value })
                }
                className="w-full border border-[var(--border)] px-3 py-2 bg-white"
              >
                <option value="">—</option>
                {(parentCategories || []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="label-overline block mb-1">{t("name")}</label>
            <input
              data-testid={`${TID}-form-name`}
              autoFocus
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full border border-[var(--border)] px-3 py-2 font-sans"
            />
          </div>
          <div>
            <label className="label-overline block mb-1">
              {t("smart_products_code")}{" "}
              <span className="text-[var(--text-secondary)] normal-case font-sans text-[0.7rem]">
                ({t("smart_products_code_hint")})
              </span>
            </label>
            <input
              data-testid={`${TID}-form-code`}
              value={form.code}
              onChange={(e) =>
                setForm({ ...form, code: e.target.value.toUpperCase() })
              }
              className="w-full border border-[var(--border)] px-3 py-2 font-mono-tech tracking-wider uppercase"
              maxLength={32}
            />
          </div>
          <div>
            <label className="label-overline block mb-1">
              {t("description")}
            </label>
            <textarea
              data-testid={`${TID}-form-description`}
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              rows={3}
              className="w-full border border-[var(--border)] px-3 py-2 font-sans"
            />
          </div>
          <label className="flex items-center gap-2 select-none cursor-pointer">
            <input
              data-testid={`${TID}-form-active`}
              type="checkbox"
              checked={form.is_active}
              onChange={(e) =>
                setForm({ ...form, is_active: e.target.checked })
              }
            />
            <span className="font-mono-tech text-sm">{t("active")}</span>
          </label>
          <div className="flex justify-end gap-2 pt-2 border-t border-[var(--border)]">
            <button
              type="button"
              data-testid={`${TID}-form-cancel`}
              onClick={() => {
                setOpen(false);
                setEditing(null);
              }}
              className="border border-[var(--border)] px-4 py-2 font-mono-tech text-sm"
            >
              {t("cancel")}
            </button>
            <button
              type="submit"
              data-testid={`${TID}-form-submit`}
              disabled={saving}
              className="bg-[var(--text-primary)] text-white px-5 py-2 font-display font-bold tracking-wide disabled:opacity-60"
            >
              {saving ? "…" : t("save")}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
